import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { Wind, ChevronRight, ChevronLeft, Calculator, Save, Printer, Trash2, AlertTriangle, CheckCircle } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer } from 'recharts';
import { toast } from 'react-toastify';

// ── Constants ──────────────────────────────────────────────
const SOURCE_TYPES = [
  { value: 'point',     label: 'Point Source (Vertical Stack)', desc: 'Standard vertical stack — boilers, engines, process vents, stacks.' },
  { value: 'capped',    label: 'Capped Stack',                  desc: 'Vertical stack with rain cap — no plume rise, momentum or buoyancy ignored.' },
  { value: 'horizontal',label: 'Horizontal Stack',              desc: 'Horizontal discharge — fans, roof exhausts, louvered vents.' },
  { value: 'flare',     label: 'Flare',                         desc: 'Elevated flare — uses AERSCREEN flare equations (Eq. 1 and 2).' },
  { value: 'volume',    label: 'Volume Source',                  desc: 'Distributed release — fugitive emissions, building exhausts, open tanks.' },
  { value: 'area_rect', label: 'Rectangular Area Source',       desc: 'Surface area release — storage piles, landfills, impoundments.' },
  { value: 'area_circ', label: 'Circular Area Source',          desc: 'Circular surface release — ponds, lagoons, circular storage.' },
];

const POLLUTANTS = [
  { value: 'PM2.5',   label: 'PM2.5',          naaqs: [{ period:'24hr', std:35 }, { period:'annual', std:12 }] },
  { value: 'PM10',    label: 'PM10',            naaqs: [{ period:'24hr', std:150 }] },
  { value: 'NO2',     label: 'NO2',             naaqs: [{ period:'1hr', std:188 }, { period:'annual', std:100 }] },
  { value: 'SO2',     label: 'SO2',             naaqs: [{ period:'1hr', std:196 }] },
  { value: 'CO',      label: 'CO',              naaqs: [{ period:'1hr', std:40000 }, { period:'8hr', std:10000 }] },
  { value: 'H2S',     label: 'H2S',             naaqs: [] },
  { value: 'HCl',     label: 'HCl',             naaqs: [] },
  { value: 'NH3',     label: 'NH3',             naaqs: [] },
  { value: 'VOC',     label: 'VOC (Total)',      naaqs: [] },
  { value: 'HAP',     label: 'HAP (specify)',    naaqs: [] },
  { value: 'other',   label: 'Other Pollutant',  naaqs: [] },
];

const LAND_USE = [
  { value: 'user',               label: 'User-specified (enter values below)' },
  { value: 'water',              label: 'Water' },
  { value: 'deciduous_forest',   label: 'Deciduous Forest' },
  { value: 'coniferous_forest',  label: 'Coniferous Forest' },
  { value: 'swamp',              label: 'Swamp' },
  { value: 'cultivated_land',    label: 'Cultivated Land' },
  { value: 'grassland',          label: 'Grassland' },
  { value: 'urban',              label: 'Urban' },
  { value: 'desert_shrubland',   label: 'Desert Shrubland' },
];

const STABILITY_LABELS = { A:'A — Very Unstable', B:'B — Moderately Unstable', C:'C — Slightly Unstable', D:'D — Neutral', E:'E — Slightly Stable', F:'F — Stable' };

const inp = { width:'100%', padding:'9px 12px', borderRadius:8, border:'1px solid #E2E8F0', fontFamily:'var(--font-body)', fontSize:14, boxSizing:'border-box', background:'white' };
const lbl = { fontSize:12, fontWeight:600, color:'#374151', fontFamily:'var(--font-body)', display:'block', marginBottom:4 };
const hint = { fontSize:11, color:'#94a3b8', fontFamily:'var(--font-body)', marginTop:2 };
const grid2 = { display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:14, marginBottom:14 };
const grid3 = { display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:14, marginBottom:14 };

const Field = ({ label, hint: h, children }) => (
  <div>
    <label style={lbl}>{label}</label>
    {children}
    {h && <div style={hint}>{h}</div>}
  </div>
);

const SectionTitle = ({ children }) => (
  <div style={{ fontFamily:'var(--font-heading)', fontSize:14, fontWeight:700, color:'#0A1628', marginBottom:12, marginTop:8, paddingBottom:6, borderBottom:'1px solid #F1F5F9' }}>{children}</div>
);

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'];


// ── Contour Plot Component ──────────────────────────────────
export default function AERScreen() {
  const [step, setStep] = useState(1);
  const [facilities, setFacilities] = useState([]);
  const [savedCalcs, setSavedCalcs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [showSaved, setShowSaved] = useState(false);
  const printRef = useRef();

  const EMPTY = {
    facility_id:'', source_name:'', source_type:'',
    // Source params
    emission_rate_gs:'', emission_rate_unit:'gs',
    stack_height_m:'', stack_diameter_m:'', stack_temp_k:'',
    stack_temp_input:'F', stack_velocity_ms:'', flow_rate_acfm:'', flow_input:'velocity',
    // Flare
    heat_release_calps:'', heat_loss_fraction:'0.55',
    // Volume / area
    release_height_m:'0', initial_lateral_dim_m:'', initial_vertical_dim_m:'1.5',
    area_length_m:'', area_width_m:'', area_radius_m:'',
    // Building downwash
    building_downwash: false,
    building_height_m:'', building_max_dim_m:'', building_min_dim_m:'',
    building_orient_deg:'', stack_direction_deg:'', stack_building_dist_m:'',
    // Met
    urban_rural:'rural', urban_population:'',
    min_temp_f:'', max_temp_f:'',
    min_wind_speed_ms:'0.5', anemometer_height_m:'10',
    land_use_type:'cultivated_land',
    albedo:'', bowen_ratio:'', surface_roughness_m:'',
    // Terrain
    probe_distance_m:'10000', ambient_distance_m:'1',
    source_elevation_m:'0', use_flagpole:false, flagpole_height_m:'',
    // Pollutant
    pollutant:'', pollutant_custom:'',
    naaqs_standard_ug_m3:'', naaqs_averaging_period:'1hr',
    // Other
    location_state:'', notes:'',
  };

  const [form, setForm] = useState(EMPTY);
  const f = (u) => setForm(prev => ({ ...prev, ...u }));

  const isPoint = ['point','capped','horizontal'].includes(form.source_type);
  const isFlare = form.source_type === 'flare';
  const isVolume = form.source_type === 'volume';
  const isArea = ['area_rect','area_circ'].includes(form.source_type);

  useEffect(() => { loadInit(); }, []);

  const loadInit = async () => {
    try {
      const [facRes, calcRes] = await Promise.all([
        api.get('/facilities'),
        api.get('/aerscreen/calculations'),
      ]);
      setFacilities(facRes.data.facilities || []);
      setSavedCalcs(calcRes.data.calculations || []);
    } catch(e) { console.error(e); }
  };

  // Convert emission rate to g/s
  const getEmissionGs = () => {
    const rate = parseFloat(form.emission_rate_gs);
    if (form.emission_rate_unit === 'gs') return rate;
    if (form.emission_rate_unit === 'lbhr') return rate * 0.125998;
    if (form.emission_rate_unit === 'tyr') return rate * 28.3168 / (365*24);
    if (form.emission_rate_unit === 'kghr') return rate / 3.6;
    return rate;
  };

  // Convert temp to Kelvin
  const getTempK = (tempF) => tempF ? parseFloat(tempF) + 459.67 : null;

  // Convert flow rate ACFM to velocity m/s
  const getVelocity = () => {
    if (form.flow_input === 'velocity') return parseFloat(form.stack_velocity_ms) || 0;
    const Q_acfm = parseFloat(form.flow_rate_acfm) || 0;
    const D_m = parseFloat(form.stack_diameter_m) || 1;
    const A = Math.PI * (D_m/2) * (D_m/2); // m²
    const Q_m3s = Q_acfm * 0.000471947;
    return Q_m3s / A;
  };

  const calculate = async (save=false) => {
    try {
      setLoading(true);
      const payload = {
        ...form,
        emission_rate_gs: getEmissionGs(),
        stack_velocity_ms: getVelocity(),
        stack_temp_k: form.stack_temp_input === 'F' ? getTempK(form.min_temp_f || form.stack_temp_k) + (parseFloat(form.stack_temp_k||0) - parseFloat(form.min_temp_f||0)) : parseFloat(form.stack_temp_k),
        min_temp_k: form.min_temp_f ? parseFloat(form.min_temp_f) + 459.67 : 250,
        max_temp_k: form.max_temp_f ? parseFloat(form.max_temp_f) + 459.67 : 310,
        save_calculation: save,
      };

      // Recalculate stack temp properly
      if (form.stack_temp_input === 'F') {
        payload.stack_temp_k = parseFloat(form.stack_temp_k) + 459.67;
      } else {
        payload.stack_temp_k = parseFloat(form.stack_temp_k);
      }

      const res = await api.post('/aerscreen/calculate', payload);
      setResults(res.data);
      if (save) { toast.success('Calculation saved'); loadInit(); }
      setStep(4);
    } catch(e) {
      toast.error('Calculation failed: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  };

  const loadSaved = (calc) => {
    setForm({ ...EMPTY, ...(calc.inputs_json || {}), source_name: calc.source_name, source_type: calc.source_type });
    setResults(calc.results_json);
    setStep(4);
    setShowSaved(false);
  };

  const deleteSaved = async (id) => {
    if (!window.confirm('Delete?')) return;
    await api.delete(`/aerscreen/calculations/${id}`);
    loadInit(); toast.success('Deleted');
  };

  const selectedPollutant = POLLUTANTS.find(p => p.value === form.pollutant);
  const concColor = (conc, std) => {
    if (!std) return '#374151';
    const pct = conc / std * 100;
    if (pct > 100) return '#DC2626';
    if (pct > 80) return '#D97706';
    return '#16A34A';
  };

  const stepLabels = ['Source Type','Source Parameters','Met & Options','Results'];

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20, flexWrap:'wrap', gap:10 }}>
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:9, marginBottom:3 }}>
            <Wind size={20} color="#0066FF" />
            <h1 style={{ fontFamily:'var(--font-heading)', fontSize:20, fontWeight:800, color:'#0A1628', margin:0 }}>Dispersion Screening (AERSCREEN)</h1>
          </div>
          <div style={{ fontSize:12, color:'#6B7280', fontFamily:'var(--font-body)' }}>Gaussian plume screening · AERMOD algorithms · 7 source types · NAAQS comparison</div>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={() => setShowSaved(!showSaved)} style={{ display:'flex', alignItems:'center', gap:5, padding:'7px 12px', borderRadius:7, border:'1px solid #E2E8F0', background:'white', cursor:'pointer', fontFamily:'var(--font-body)', fontSize:13 }}>
            <Save size={13} /> Saved ({savedCalcs.length})
          </button>
          {step > 1 && <button onClick={() => { setStep(1); setResults(null); setForm(EMPTY); }} style={{ padding:'7px 12px', borderRadius:7, border:'1px solid #E2E8F0', background:'white', cursor:'pointer', fontFamily:'var(--font-body)', fontSize:13 }}>New</button>}
        </div>
      </div>

      {/* Saved */}
      {showSaved && (
        <div className="card" style={{ marginBottom:18 }}>
          <div style={{ fontFamily:'var(--font-heading)', fontSize:14, fontWeight:700, color:'#0A1628', marginBottom:12 }}>Saved Calculations</div>
          {savedCalcs.length === 0 ? <div style={{ textAlign:'center', color:'#94a3b8', fontFamily:'var(--font-body)', fontSize:13, padding:'16px 0' }}>No saved calculations</div> :
            savedCalcs.map(calc => (
              <div key={calc.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 0', borderBottom:'1px solid #F1F5F9' }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:'#0A1628', fontFamily:'var(--font-body)' }}>{calc.source_name}</div>
                  <div style={{ fontSize:11, color:'#6B7280', fontFamily:'var(--font-body)' }}>
                    {calc.facility_name||'No facility'} · {calc.source_type} · {calc.pollutant||'—'} ·
                    <strong> {parseFloat(calc.max_conc_ug_m3||0).toFixed(2)} µg/m³</strong> at {parseFloat(calc.max_conc_distance_m||0).toFixed(0)}m
                  </div>
                </div>
                <button onClick={() => loadSaved(calc)} style={{ padding:'4px 10px', borderRadius:6, border:'1px solid #0066FF', background:'#EFF6FF', color:'#0066FF', cursor:'pointer', fontFamily:'var(--font-body)', fontSize:12, fontWeight:600 }}>Load</button>
                <button onClick={() => deleteSaved(calc.id)} style={{ background:'none', border:'none', cursor:'pointer', color:'#DC2626', padding:3 }}><Trash2 size={13} /></button>
              </div>
            ))
          }
        </div>
      )}

      {/* Step bar */}
      {step < 4 && (
        <div style={{ display:'flex', gap:0, marginBottom:22, borderRadius:9, overflow:'hidden' }}>
          {stepLabels.slice(0,3).map((title,i) => (
            <div key={i} style={{ flex:1, padding:'9px 6px', background: step===i+1?'#0066FF': step>i+1?'#E0EDFF':'#F8FAFC', borderRight:i<2?'1px solid white':'none', textAlign:'center' }}>
              <div style={{ fontSize:9, fontWeight:700, color: step===i+1?'white': step>i+1?'#0066FF':'#94a3b8', textTransform:'uppercase', letterSpacing:'0.5px', fontFamily:'var(--font-body)' }}>Step {i+1}</div>
              <div style={{ fontSize:11, fontWeight:600, color: step===i+1?'white': step>i+1?'#0066FF':'#374151', fontFamily:'var(--font-body)' }}>{title}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── STEP 1: SOURCE TYPE ── */}
      {step === 1 && (
        <div>
          <SectionTitle>Select Source Type</SectionTitle>
          <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:20 }}>
            {SOURCE_TYPES.map(t => (
              <div key={t.value} onClick={() => f({ source_type: t.value })}
                style={{ padding:'12px 14px', borderRadius:8, border:`1px solid ${form.source_type===t.value?'#0066FF':'#E2E8F0'}`, borderLeft:`4px solid ${form.source_type===t.value?'#0066FF':'#E2E8F0'}`, background: form.source_type===t.value?'#EFF6FF':'white', cursor:'pointer', display:'flex', alignItems:'center', gap:12 }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:700, color: form.source_type===t.value?'#0066FF':'#0A1628', fontFamily:'var(--font-body)', marginBottom:2 }}>{t.label}</div>
                  <div style={{ fontSize:11, color:'#6B7280', fontFamily:'var(--font-body)' }}>{t.desc}</div>
                </div>
                {form.source_type===t.value && <div style={{ width:8, height:8, borderRadius:'50%', background:'#0066FF', flexShrink:0 }} />}
              </div>
            ))}
          </div>

          <div style={grid2}>
            <Field label="Source ID / Name *"><input type="text" value={form.source_name} onChange={e => f({ source_name: e.target.value })} placeholder="e.g. BOILER-001" style={inp} /></Field>
            <Field label="Facility (optional)">
              <select value={form.facility_id} onChange={e => f({ facility_id: e.target.value })} style={inp}>
                <option value="">Select facility</option>
                {facilities.map(fac => <option key={fac.id} value={fac.id}>{fac.facility_name||fac.name}</option>)}
              </select>
            </Field>
          </div>

          <button disabled={!form.source_type || !form.source_name} onClick={() => setStep(2)} className="btn btn-primary" style={{ display:'flex', alignItems:'center', gap:6, marginBottom:32 }}>
            Next: Source Parameters <ChevronRight size={14} />
          </button>
        </div>
      )}

      {/* ── STEP 2: SOURCE PARAMETERS ── */}
      {step === 2 && (
        <div>
          <SectionTitle>Emission Rate</SectionTitle>
          <div style={grid2}>
            <Field label="Emission Rate *" hint="Enter the rate and select units">
              <input type="number" value={form.emission_rate_gs} onChange={e => f({ emission_rate_gs: e.target.value })} placeholder="e.g. 1.5" style={inp} />
            </Field>
            <Field label="Units">
              <select value={form.emission_rate_unit} onChange={e => f({ emission_rate_unit: e.target.value })} style={inp}>
                <option value="gs">g/s</option>
                <option value="lbhr">lb/hr</option>
                <option value="kghr">kg/hr</option>
                <option value="tyr">ton/yr</option>
              </select>
            </Field>
          </div>

          <SectionTitle>Pollutant</SectionTitle>
          <div style={grid2}>
            <Field label="Pollutant">
              <select value={form.pollutant} onChange={e => {
                const p = POLLUTANTS.find(p => p.value === e.target.value);
                const firstNaaqs = p?.naaqs?.[0];
                f({ pollutant: e.target.value, naaqs_standard_ug_m3: firstNaaqs?.std||'', naaqs_averaging_period: firstNaaqs?.period||'1hr' });
              }} style={inp}>
                <option value="">Select pollutant (optional)</option>
                {POLLUTANTS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </Field>
            {form.pollutant === 'other' && (
              <Field label="Pollutant Name"><input type="text" value={form.pollutant_custom} onChange={e => f({ pollutant_custom: e.target.value })} placeholder="e.g. Benzene" style={inp} /></Field>
            )}
            {selectedPollutant?.naaqs?.length > 0 && (
              <>
                <Field label="NAAQS Standard (µg/m³)" hint="Auto-populated — edit if using state standard">
                  <input type="number" value={form.naaqs_standard_ug_m3} onChange={e => f({ naaqs_standard_ug_m3: e.target.value })} style={inp} />
                </Field>
                <Field label="Averaging Period">
                  <select value={form.naaqs_averaging_period} onChange={e => {
                    const naaqs = selectedPollutant.naaqs.find(n => n.period === e.target.value);
                    f({ naaqs_averaging_period: e.target.value, naaqs_standard_ug_m3: naaqs?.std||form.naaqs_standard_ug_m3 });
                  }} style={inp}>
                    {selectedPollutant.naaqs.map(n => <option key={n.period} value={n.period}>{n.period} — {n.std} µg/m³</option>)}
                  </select>
                </Field>
              </>
            )}
          </div>

          {/* Point / Capped / Horizontal stack params */}
          {isPoint && (
            <>
              <SectionTitle>Stack Parameters</SectionTitle>
              <div style={grid2}>
                <Field label="Stack Height (m) *" hint="From ground to stack exit"><input type="number" value={form.stack_height_m} onChange={e => f({ stack_height_m: e.target.value })} placeholder="e.g. 30" style={inp} /></Field>
                <Field label="Stack Diameter (m) *" hint="Inside diameter at stack exit"><input type="number" value={form.stack_diameter_m} onChange={e => f({ stack_diameter_m: e.target.value })} placeholder="e.g. 0.5" style={inp} /></Field>
              </div>
              <div style={grid2}>
                <Field label="Stack Exit Temperature" hint="°F or K — select unit">
                  <div style={{ display:'flex', gap:6 }}>
                    <input type="number" value={form.stack_temp_k} onChange={e => f({ stack_temp_k: e.target.value })} placeholder={form.stack_temp_input==='F'?'e.g. 300':'e.g. 422'} style={{ ...inp, flex:1 }} />
                    <select value={form.stack_temp_input} onChange={e => f({ stack_temp_input: e.target.value })} style={{ ...inp, width:60 }}>
                      <option value="F">°F</option>
                      <option value="K">K</option>
                    </select>
                  </div>
                </Field>
                <Field label="Exit Velocity Input Method">
                  <select value={form.flow_input} onChange={e => f({ flow_input: e.target.value })} style={inp}>
                    <option value="velocity">Exit Velocity (m/s)</option>
                    <option value="acfm">Flow Rate (ACFM)</option>
                  </select>
                </Field>
                {form.flow_input === 'velocity' ? (
                  <Field label="Exit Velocity (m/s) *"><input type="number" value={form.stack_velocity_ms} onChange={e => f({ stack_velocity_ms: e.target.value })} placeholder="e.g. 12" style={inp} /></Field>
                ) : (
                  <Field label="Flow Rate (ACFM) *"><input type="number" value={form.flow_rate_acfm} onChange={e => f({ flow_rate_acfm: e.target.value })} placeholder="e.g. 5000" style={inp} /></Field>
                )}
              </div>

              {/* Building downwash */}
              <SectionTitle>Building Downwash (PRIME)</SectionTitle>
              <div style={{ marginBottom:14 }}>
                <Field label="Include Building Downwash?">
                  <select value={form.building_downwash} onChange={e => f({ building_downwash: e.target.value === 'true' })} style={{ ...inp, maxWidth:300 }}>
                    <option value="false">No — open terrain, no nearby buildings</option>
                    <option value="true">Yes — stack near building, use BPIPPRM/PRIME</option>
                  </select>
                </Field>
              </div>
              {form.building_downwash && (
                <div style={grid2}>
                  <Field label="Building Height (m)"><input type="number" value={form.building_height_m} onChange={e => f({ building_height_m: e.target.value })} style={inp} /></Field>
                  <Field label="Maximum Horizontal Dimension (m)"><input type="number" value={form.building_max_dim_m} onChange={e => f({ building_max_dim_m: e.target.value })} style={inp} /></Field>
                  <Field label="Minimum Horizontal Dimension (m)"><input type="number" value={form.building_min_dim_m} onChange={e => f({ building_min_dim_m: e.target.value })} style={inp} /></Field>
                  <Field label="Max Dimension Angle to True North (°)" hint="0–179 degrees"><input type="number" value={form.building_orient_deg} onChange={e => f({ building_orient_deg: e.target.value })} style={inp} /></Field>
                  <Field label="Direction of Stack from Building Center (°)" hint="0–360 degrees"><input type="number" value={form.stack_direction_deg} onChange={e => f({ stack_direction_deg: e.target.value })} style={inp} /></Field>
                  <Field label="Distance: Stack to Building Center (m)"><input type="number" value={form.stack_building_dist_m} onChange={e => f({ stack_building_dist_m: e.target.value })} style={inp} /></Field>
                </div>
              )}
            </>
          )}

          {/* Flare params */}
          {isFlare && (
            <>
              <SectionTitle>Flare Parameters</SectionTitle>
              <div style={grid2}>
                <Field label="Flare Stack Height (m) *" hint="Physical height of flare tip above ground"><input type="number" value={form.stack_height_m} onChange={e => f({ stack_height_m: e.target.value })} placeholder="e.g. 30" style={inp} /></Field>
                <Field label="Total Heat Release Rate (cal/sec) *" hint="Net combustion heat release at flare tip"><input type="number" value={form.heat_release_calps} onChange={e => f({ heat_release_calps: e.target.value })} placeholder="e.g. 1000000" style={inp} /></Field>
                <Field label="Radiative Heat Loss Fraction" hint="Default: 0.55 (SCREEN3 default)">
                  <input type="number" value={form.heat_loss_fraction} onChange={e => f({ heat_loss_fraction: e.target.value })} placeholder="0.55" style={inp} />
                </Field>
              </div>
              <div style={{ padding:'10px 14px', background:'#EFF6FF', border:'1px solid #BFDBFE', borderRadius:8, marginBottom:14, fontSize:12, color:'#1E40AF', fontFamily:'var(--font-body)' }}>
                Effective stack height and diameter calculated using AERSCREEN Equations 1 and 2. Exit velocity and temperature default to 20 m/s and 1,273 K per SCREEN3.
              </div>
            </>
          )}

          {/* Volume params */}
          {isVolume && (
            <>
              <SectionTitle>Volume Source Parameters</SectionTitle>
              <div style={grid2}>
                <Field label="Center of Volume Height (m) *" hint="Height of center of volume above ground"><input type="number" value={form.release_height_m} onChange={e => f({ release_height_m: e.target.value })} placeholder="e.g. 3" style={inp} /></Field>
                <Field label="Initial Lateral Dimension (m) *" hint="Horizontal extent of volume (σy0 = dim/4.3)"><input type="number" value={form.initial_lateral_dim_m} onChange={e => f({ initial_lateral_dim_m: e.target.value })} placeholder="e.g. 10" style={inp} /></Field>
                <Field label="Initial Vertical Dimension (m) *" hint="Vertical extent of volume (σz0 = dim/2.15)"><input type="number" value={form.initial_vertical_dim_m} onChange={e => f({ initial_vertical_dim_m: e.target.value })} placeholder="e.g. 3" style={inp} /></Field>
              </div>
            </>
          )}

          {/* Area params */}
          {isArea && (
            <>
              <SectionTitle>{form.source_type === 'area_rect' ? 'Rectangular' : 'Circular'} Area Source Parameters</SectionTitle>
              <div style={grid2}>
                <Field label="Release Height Above Ground (m)" hint="Usually 0 for ground-level source"><input type="number" value={form.release_height_m} onChange={e => f({ release_height_m: e.target.value })} placeholder="0" style={inp} /></Field>
                {form.source_type === 'area_rect' ? (
                  <>
                    <Field label="Long Side (m) *" hint="Length (x-direction)"><input type="number" value={form.area_length_m} onChange={e => f({ area_length_m: e.target.value })} style={inp} /></Field>
                    <Field label="Short Side (m) *" hint="Width (y-direction)"><input type="number" value={form.area_width_m} onChange={e => f({ area_width_m: e.target.value })} style={inp} /></Field>
                  </>
                ) : (
                  <Field label="Radius (m) *"><input type="number" value={form.area_radius_m} onChange={e => f({ area_radius_m: e.target.value })} style={inp} /></Field>
                )}
                <Field label="Initial Vertical Dimension (m)" hint="Default 1.5 m"><input type="number" value={form.initial_vertical_dim_m} onChange={e => f({ initial_vertical_dim_m: e.target.value })} placeholder="1.5" style={inp} /></Field>
              </div>
            </>
          )}

          <div style={{ display:'flex', gap:10 }}>
            <button onClick={() => setStep(1)} style={{ display:'flex', alignItems:'center', gap:5, padding:'8px 14px', borderRadius:8, border:'1px solid #E2E8F0', background:'white', cursor:'pointer', fontFamily:'var(--font-body)', fontSize:14 }}><ChevronLeft size={14} /> Back</button>
            <button disabled={!form.emission_rate_gs} onClick={() => setStep(3)} className="btn btn-primary" style={{ display:'flex', alignItems:'center', gap:6 }}>
              Next: Met & Options <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 3: MET & OPTIONS ── */}
      {step === 3 && (
        <div>
          <SectionTitle>Site Classification</SectionTitle>
          <div style={grid2}>
            <Field label="Rural or Urban?" hint="Urban: buildings/trees within ~3km. Rural: open terrain.">
              <select value={form.urban_rural} onChange={e => f({ urban_rural: e.target.value })} style={inp}>
                <option value="rural">Rural — flat open terrain</option>
                <option value="urban">Urban — buildings or trees nearby</option>
              </select>
            </Field>
            {form.urban_rural === 'urban' && (
              <Field label="Urban Population" hint="Required for AERMOD urban option"><input type="number" value={form.urban_population} onChange={e => f({ urban_population: e.target.value })} placeholder="e.g. 250000" style={inp} /></Field>
            )}
            <Field label="State"><select value={form.location_state} onChange={e => f({ location_state: e.target.value })} style={inp}><option value="">Select state</option>{US_STATES.map(s => <option key={s} value={s}>{s}</option>)}</select></Field>
          </div>

          <SectionTitle>Ambient Temperatures (MAKEMET)</SectionTitle>
          <div style={grid2}>
            <Field label="Minimum Ambient Temperature (°F)" hint="Record minimum for the area — conservative approach"><input type="number" value={form.min_temp_f} onChange={e => f({ min_temp_f: e.target.value })} placeholder="e.g. 0 (default: 26°F / 250K)" style={inp} /></Field>
            <Field label="Maximum Ambient Temperature (°F)" hint="Record maximum for the area"><input type="number" value={form.max_temp_f} onChange={e => f({ max_temp_f: e.target.value })} placeholder="e.g. 100 (default: 98°F / 310K)" style={inp} /></Field>
          </div>

          <SectionTitle>Meteorological Parameters</SectionTitle>
          <div style={grid2}>
            <Field label="Minimum Wind Speed (m/s)" hint="Default: 0.5 m/s per AERSCREEN guidance"><input type="number" value={form.min_wind_speed_ms} onChange={e => f({ min_wind_speed_ms: e.target.value })} style={inp} /></Field>
            <Field label="Anemometer Height (m)" hint="Default: 10 m"><input type="number" value={form.anemometer_height_m} onChange={e => f({ anemometer_height_m: e.target.value })} style={inp} /></Field>
          </div>

          <SectionTitle>Surface Characteristics (MAKEMET Input)</SectionTitle>
          <div style={{ marginBottom:14 }}>
            <Field label="Land Use Type">
              <select value={form.land_use_type} onChange={e => f({ land_use_type: e.target.value })} style={inp}>
                {LAND_USE.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
            </Field>
          </div>
          {form.land_use_type === 'user' && (
            <div style={grid3}>
              <Field label="Albedo" hint="0.10–0.70. Default: 0.14"><input type="number" value={form.albedo} onChange={e => f({ albedo: e.target.value })} placeholder="e.g. 0.14" style={inp} /></Field>
              <Field label="Bowen Ratio" hint="0.1–5.0. Default: 1.5 (urban)"><input type="number" value={form.bowen_ratio} onChange={e => f({ bowen_ratio: e.target.value })} placeholder="e.g. 1.5" style={inp} /></Field>
              <Field label="Surface Roughness (m)" hint="0.001–2.0. Default: 1.0 (urban), 0.1 (open)"><input type="number" value={form.surface_roughness_m} onChange={e => f({ surface_roughness_m: e.target.value })} placeholder="e.g. 0.10" style={inp} /></Field>
            </div>
          )}

          <SectionTitle>Receptor Options</SectionTitle>
          <div style={grid2}>
            <Field label="Probe Distance / Maximum Downwind Distance (m)" hint="Default: 10,000 m. Reset to nearest 25-m multiple.">
              <input type="number" value={form.probe_distance_m} onChange={e => f({ probe_distance_m: e.target.value })} style={inp} />
            </Field>
            <Field label="Minimum Ambient Distance (m)" hint="Default: 1 m for point sources">
              <input type="number" value={form.ambient_distance_m} onChange={e => f({ ambient_distance_m: e.target.value })} style={inp} />
            </Field>
            <Field label="Source Base Elevation (m)" hint="Ground elevation at source. Default: 0 m (flat terrain)">
              <input type="number" value={form.source_elevation_m} onChange={e => f({ source_elevation_m: e.target.value })} placeholder="0" style={inp} />
            </Field>
            <Field label="Use Flagpole Receptors?">
              <select value={form.use_flagpole} onChange={e => f({ use_flagpole: e.target.value === 'true' })} style={inp}>
                <option value="false">No flagpole receptors</option>
                <option value="true">Yes — flagpole receptors</option>
              </select>
            </Field>
            {form.use_flagpole && (
              <Field label="Flagpole Height (m)"><input type="number" value={form.flagpole_height_m} onChange={e => f({ flagpole_height_m: e.target.value })} placeholder="e.g. 1.5" style={inp} /></Field>
            )}
          </div>

          <Field label="Notes / Modeling Assumptions">
            <textarea value={form.notes} onChange={e => f({ notes: e.target.value })} rows={2} placeholder="Document assumptions, deviations from defaults, permit context..." style={{ ...inp, resize:'vertical', marginBottom:14 }} />
          </Field>

          <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
            <button onClick={() => setStep(2)} style={{ display:'flex', alignItems:'center', gap:5, padding:'8px 14px', borderRadius:8, border:'1px solid #E2E8F0', background:'white', cursor:'pointer', fontFamily:'var(--font-body)', fontSize:14 }}><ChevronLeft size={14} /> Back</button>
            <button onClick={() => calculate(false)} disabled={loading} className="btn btn-primary" style={{ display:'flex', alignItems:'center', gap:6 }}>
              <Calculator size={14} /> {loading ? 'Running...' : 'Run Screening'}
            </button>
            <button onClick={() => calculate(true)} disabled={loading}
              style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 16px', borderRadius:8, border:'1px solid #16A34A', background:'#F0FDF4', color:'#16A34A', cursor:'pointer', fontFamily:'var(--font-body)', fontSize:14, fontWeight:600 }}>
              <Save size={14} /> Run & Save
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 4: RESULTS ── */}
      {step === 4 && results && (
        <div ref={printRef}>
          <style>{`@media print { .no-print{display:none!important;} body{background:white!important;} }`}</style>

          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:18 }} className="no-print">
            <div style={{ fontFamily:'var(--font-heading)', fontSize:16, fontWeight:700, color:'#0A1628' }}>Screening Results</div>
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={() => window.print()} style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 12px', borderRadius:8, border:'1px solid #E2E8F0', background:'white', cursor:'pointer', fontFamily:'var(--font-body)', fontSize:13 }}><Printer size={13} /> Print</button>
              <button onClick={() => calculate(true)} style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 12px', borderRadius:8, border:'1px solid #16A34A', background:'#F0FDF4', color:'#16A34A', cursor:'pointer', fontFamily:'var(--font-body)', fontSize:13, fontWeight:600 }}><Save size={13} /> Save</button>
            </div>
          </div>

          {/* Report header */}
          <div className="card" style={{ padding:20, marginBottom:14 }}>
            <div style={{ textAlign:'center', marginBottom:16, paddingBottom:14, borderBottom:'2px solid #0066FF' }}>
              <div style={{ fontFamily:'var(--font-heading)', fontSize:18, fontWeight:800, color:'#0066FF', marginBottom:3 }}>AirComply — Dispersion Screening Analysis</div>
              <div style={{ fontSize:12, color:'#6B7280', fontFamily:'var(--font-body)' }}>
                Gaussian Plume Screening · AERMOD Dispersion Algorithms · {new Date().toLocaleDateString()}
              </div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:8 }}>
              {[
                { label:'Source ID', value: form.source_name },
                { label:'Source Type', value: SOURCE_TYPES.find(t=>t.value===form.source_type)?.label },
                { label:'Facility', value: facilities.find(fac=>fac.id==form.facility_id)?.facility_name||'Not specified' },
                { label:'Pollutant', value: form.pollutant || 'Not specified' },
                { label:'Site Classification', value: form.urban_rural === 'urban' ? 'Urban' : 'Rural' },
                { label:'Probe Distance', value: `${parseFloat(form.probe_distance_m).toLocaleString()} m` },
              ].map((item,i) => (
                <div key={i} style={{ padding:'7px 10px', background:'#F8FAFC', borderRadius:7 }}>
                  <div style={{ fontSize:9, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.5px', fontFamily:'var(--font-body)', marginBottom:2 }}>{item.label}</div>
                  <div style={{ fontSize:12, fontWeight:600, color:'#0A1628', fontFamily:'var(--font-body)' }}>{item.value||'—'}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Key result */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:14 }}>
            <div style={{ padding:20, background:'#EFF6FF', border:'2px solid #0066FF', borderRadius:10, textAlign:'center' }}>
              <div style={{ fontSize:10, fontWeight:700, color:'#6B7280', textTransform:'uppercase', letterSpacing:'0.5px', fontFamily:'var(--font-body)', marginBottom:6 }}>Maximum 1-Hour GLC</div>
              <div style={{ fontFamily:'var(--font-heading)', fontSize:36, fontWeight:800, color:'#0066FF' }}>{parseFloat(results.max_conc_ug_m3||0).toFixed(2)}</div>
              <div style={{ fontSize:13, fontWeight:600, color:'#0066FF', fontFamily:'var(--font-body)' }}>µg/m³</div>
              <div style={{ fontSize:11, color:'#6B7280', fontFamily:'var(--font-body)', marginTop:4 }}>
                at {parseFloat(results.max_conc_distance_m||0).toFixed(0)} m downwind
              </div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
              {[
                { label:'Distance to Max', value: `${parseFloat(results.max_conc_distance_km||0).toFixed(2)} km` },
                { label:'Worst Stability', value: results.worst_stability_class ? STABILITY_LABELS[results.worst_stability_class] : '—' },
                { label:'Effective Plume Height', value: results.effective_plume_height_m ? `${parseFloat(results.effective_plume_height_m).toFixed(1)} m` : '—' },
                { label:'Worst Wind Speed', value: results.worst_wind_speed_ms ? `${results.worst_wind_speed_ms} m/s` : '—' },
              ].map((item,i) => (
                <div key={i} style={{ padding:'10px 12px', background:'#F8FAFC', border:'1px solid #E2E8F0', borderRadius:8, textAlign:'center' }}>
                  <div style={{ fontSize:9, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', fontFamily:'var(--font-body)', marginBottom:4 }}>{item.label}</div>
                  <div style={{ fontSize:13, fontWeight:700, color:'#0A1628', fontFamily:'var(--font-body)' }}>{item.value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* NAAQS comparison */}
          {results.naaqs_comparison && (
            <div className="card" style={{ padding:'16px 20px', marginBottom:14, borderLeft:`4px solid ${results.naaqs_comparison.exceeds_standard?'#DC2626':'#16A34A'}` }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                {results.naaqs_comparison.exceeds_standard ?
                  <AlertTriangle size={16} color="#DC2626" /> :
                  <CheckCircle size={16} color="#16A34A" />}
                <div style={{ fontFamily:'var(--font-heading)', fontSize:14, fontWeight:700, color:'#0A1628' }}>
                  NAAQS Comparison — {results.naaqs_comparison.pollutant} ({results.naaqs_comparison.averaging_period})
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10 }}>
                <div style={{ padding:'8px 10px', background:'#F8FAFC', borderRadius:7, textAlign:'center' }}>
                  <div style={{ fontSize:10, color:'#94a3b8', fontFamily:'var(--font-body)', marginBottom:2, fontWeight:600, textTransform:'uppercase' }}>Modeled Conc.</div>
                  <div style={{ fontSize:18, fontWeight:800, color:'#0A1628', fontFamily:'var(--font-heading)' }}>{parseFloat(results.naaqs_comparison.modeled_conc||0).toFixed(2)}</div>
                  <div style={{ fontSize:11, color:'#6B7280', fontFamily:'var(--font-body)' }}>µg/m³</div>
                </div>
                <div style={{ padding:'8px 10px', background:'#F8FAFC', borderRadius:7, textAlign:'center' }}>
                  <div style={{ fontSize:10, color:'#94a3b8', fontFamily:'var(--font-body)', marginBottom:2, fontWeight:600, textTransform:'uppercase' }}>NAAQS Standard</div>
                  <div style={{ fontSize:18, fontWeight:800, color:'#0A1628', fontFamily:'var(--font-heading)' }}>{results.naaqs_comparison.standard_ug_m3}</div>
                  <div style={{ fontSize:11, color:'#6B7280', fontFamily:'var(--font-body)' }}>µg/m³</div>
                </div>
                <div style={{ padding:'8px 10px', background: results.naaqs_comparison.exceeds_standard?'#FEF2F2':'#F0FDF4', borderRadius:7, textAlign:'center' }}>
                  <div style={{ fontSize:10, color:'#94a3b8', fontFamily:'var(--font-body)', marginBottom:2, fontWeight:600, textTransform:'uppercase' }}>% of Standard</div>
                  <div style={{ fontSize:18, fontWeight:800, color: results.naaqs_comparison.exceeds_standard?'#DC2626':'#16A34A', fontFamily:'var(--font-heading)' }}>
                    {results.naaqs_comparison.percent_of_standard}%
                  </div>
                  <div style={{ fontSize:11, color: results.naaqs_comparison.exceeds_standard?'#DC2626':'#16A34A', fontFamily:'var(--font-body)', fontWeight:600 }}>
                    {results.naaqs_comparison.exceeds_standard ? 'EXCEEDS STANDARD' : 'Below Standard'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Averaging periods */}
          <div className="card" style={{ padding:'18px 20px', marginBottom:14 }}>
            <div style={{ fontFamily:'var(--font-heading)', fontSize:14, fontWeight:700, color:'#0A1628', marginBottom:12 }}>Maximum Concentrations by Averaging Period</div>
            <div style={{ fontSize:11, color:'#94a3b8', fontFamily:'var(--font-body)', marginBottom:10 }}>
              Screening ratios per AERSCREEN Section 1.1: 3-hr=1.00, 8-hr=0.90, 24-hr=0.60, annual=0.10 of 1-hr max
            </div>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ background:'#F8FAFC' }}>
                  {['Averaging Period','Max Concentration (µg/m³)','% of 1-hr Max'].map(h => (
                    <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontSize:11, fontWeight:700, color:'#6B7280', fontFamily:'var(--font-body)', textTransform:'uppercase', borderBottom:'1px solid #E2E8F0' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.entries(results.averaging_periods||{}).filter(([k,v]) => v !== null).map(([period, conc]) => (
                  <tr key={period} style={{ borderBottom:'1px solid #F8FAFC', background: period==='1hr'?'#EFF6FF':'white' }}>
                    <td style={{ padding:'9px 12px', fontSize:13, fontFamily:'var(--font-body)', fontWeight: period==='1hr'?700:400, color:'#0A1628' }}>{period}</td>
                    <td style={{ padding:'9px 12px', fontSize:13, fontFamily:'var(--font-body)', fontWeight: period==='1hr'?700:400, color: period==='1hr'?'#0066FF':'#374151' }}>{parseFloat(conc).toFixed(4)}</td>
                    <td style={{ padding:'9px 12px', fontSize:13, fontFamily:'var(--font-body)', color:'#6B7280' }}>
                      {parseFloat(conc / results.max_conc_ug_m3 * 100).toFixed(0)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Concentration vs Distance Chart */}
          {results.conc_by_distance?.length > 0 && (
            <div className="card" style={{ padding:'18px 20px', marginBottom:14 }}>
              <div style={{ fontFamily:'var(--font-heading)', fontSize:14, fontWeight:700, color:'#0A1628', marginBottom:4 }}>Concentration vs. Distance</div>
              <div style={{ fontSize:11, color:'#94a3b8', fontFamily:'var(--font-body)', marginBottom:14 }}>
                Maximum 1-hour ground level concentration along plume centerline · Stability class {results.worst_stability_class} · Wind {results.worst_wind_speed_ms} m/s
              </div>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={results.conc_by_distance.map(d => ({ dist_km: parseFloat((d.distance_m/1000).toFixed(2)), conc: parseFloat(d.conc_ug_m3.toFixed(4)) }))}
                  margin={{ top:5, right:20, left:10, bottom:5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis dataKey="dist_km" label={{ value:'Distance (km)', position:'insideBottom', offset:-2, fontSize:11, fill:'#6B7280' }} tick={{ fontSize:11, fill:'#6B7280' }} />
                  <YAxis label={{ value:'µg/m³', angle:-90, position:'insideLeft', offset:10, fontSize:11, fill:'#6B7280' }} tick={{ fontSize:11, fill:'#6B7280' }} />
                  <Tooltip formatter={(v) => [`${v} µg/m³`, '1-hr Max GLC']} labelFormatter={(l) => `Distance: ${l} km`}
                    contentStyle={{ fontFamily:'var(--font-body)', fontSize:12, borderRadius:8, border:'1px solid #E2E8F0' }} />
                  {results.naaqs_comparison && (
                    <ReferenceLine y={results.naaqs_comparison.standard_ug_m3} stroke="#DC2626" strokeDasharray="4 2"
                      label={{ value:`NAAQS ${results.naaqs_comparison.averaging_period}`, position:'right', fontSize:10, fill:'#DC2626' }} />
                  )}
                  <ReferenceLine x={parseFloat((results.max_conc_distance_m/1000).toFixed(2))} stroke="#0066FF" strokeDasharray="3 3"
                    label={{ value:'Max', position:'top', fontSize:10, fill:'#0066FF' }} />
                  <Line type="monotone" dataKey="conc" stroke="#0066FF" strokeWidth={2} dot={false} activeDot={{ r:4 }} />
                </LineChart>
              </ResponsiveContainer>

              {/* Concentration table below chart */}
              <div style={{ marginTop:14, maxHeight:200, overflowY:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead>
                    <tr style={{ background:'#F8FAFC', position:'sticky', top:0 }}>
                      {['Distance (m)','Distance (km)','1-hr Max (µg/m³)','% of NAAQS'].map(h => (
                        <th key={h} style={{ padding:'6px 12px', textAlign:'left', fontSize:10, fontWeight:700, color:'#6B7280', fontFamily:'var(--font-body)', borderBottom:'1px solid #E2E8F0', textTransform:'uppercase' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {results.conc_by_distance.map((row, i) => {
                      const pctStd = results.naaqs_comparison ? (row.conc_ug_m3 / results.naaqs_comparison.standard_ug_m3 * 100).toFixed(1) : null;
                      const isMax = row.distance_m === results.max_conc_distance_m;
                      return (
                        <tr key={i} style={{ borderBottom:'1px solid #F8FAFC', background: isMax?'#EFF6FF':'white' }}>
                          <td style={{ padding:'6px 12px', fontSize:12, fontFamily:'var(--font-body)', color:'#374151', fontWeight:isMax?700:400 }}>{parseFloat(row.distance_m).toFixed(0)}</td>
                          <td style={{ padding:'6px 12px', fontSize:12, fontFamily:'var(--font-body)', color:'#374151' }}>{(row.distance_m/1000).toFixed(2)}</td>
                          <td style={{ padding:'6px 12px', fontSize:12, fontFamily:'var(--font-body)', color:isMax?'#0066FF':'#374151', fontWeight:isMax?700:400 }}>{parseFloat(row.conc_ug_m3).toFixed(4)}</td>
                          <td style={{ padding:'6px 12px', fontSize:12, fontFamily:'var(--font-body)', color: pctStd > 100?'#DC2626':pctStd > 80?'#D97706':'#16A34A' }}>
                            {pctStd ? `${pctStd}%` : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Disclaimer */}
          <div style={{ padding:'12px 16px', background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:8, marginBottom:12 }}>
            <div style={{ fontSize:11, color:'#92400E', fontFamily:'var(--font-body)', lineHeight:1.7 }}>
              <strong>Disclaimer:</strong> Results represent worst-case screening-level estimates using Gaussian plume dispersion equations based on AERMOD algorithms. This tool is not EPA AERSCREEN and results have not been validated against the EPA model. Terrain effects, building downwash (PRIME), and fumigation are not included in this screening. Results are conservative and represent upper-bound concentrations. A refined AERMOD analysis may be required for permit applications. Results should be reviewed by a licensed Professional Engineer (PE) before use in regulatory submittals.
            </div>
          </div>

          <div style={{ padding:'12px 16px', background:'#EFF6FF', border:'1px solid #BFDBFE', borderRadius:8 }}>
            <div style={{ fontSize:11, color:'#1E40AF', fontFamily:'var(--font-body)', lineHeight:1.7 }}>
              <strong>Regulatory Reference:</strong> Screening analysis based on AERMOD Gaussian plume dispersion algorithms (U.S. EPA, 2021). {results.regulatory_citation}. NAAQS standards per 40 CFR Part 50. Averaging period scaling ratios per EPA screening guidance (EPA-454/B-21-005). For regulatory permit applications, run EPA AERSCREEN or AERMOD with site-specific meteorological data per 40 CFR Part 51 Appendix W — Guideline on Air Quality Models.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
